/**
 * Module-level WebSocket connectivity signal, fed by the graphql-ws client
 * `on:` handlers in `~/graphql/index.tsx`. Kept React-free and tiny so any
 * consumer (e.g. the aiFeedback polling fallback, FOX-3701) can watch the
 * socket lifecycle without importing the Apollo client setup.
 *
 * It also carries the one outage signal for the live connection: a socket
 * that keeps failing to reconnect. Individual drops are handled (graphql-ws
 * retries and resubscribes), so they are breadcrumbs, not errors; only a tab
 * that cannot get back to `connected` after several attempts is reported —
 * once, through `onWsReconnectStuck` — which is the shape a real outage has.
 */

export type WsConnectivityState = "idle" | "connecting" | "connected" | "closed";

/** Consecutive `connecting` attempts without a `connected` before the stuck signal fires. */
export const WS_STUCK_AFTER_ATTEMPTS = 3;

let state: WsConnectivityState = "idle";
let failedAttempts = 0;
let stuckReported = false;
const connectedListeners = new Set<() => void>();
const stateListeners = new Set<
  (next: WsConnectivityState, previous: WsConnectivityState) => void
>();
const stuckListeners = new Set<(attempts: number) => void>();

export function getWsConnectivityState(): WsConnectivityState {
  return state;
}

/**
 * Subscribe to `connected` events (each successful socket connect, including
 * reconnects). Returns an unsubscribe function.
 */
export function onWsConnected(listener: () => void): () => void {
  connectedListeners.add(listener);
  return () => {
    connectedListeners.delete(listener);
  };
}

/** Subscribe to every state transition (`connecting`, `connected`, `closed`). */
export function onWsStateChange(
  listener: (next: WsConnectivityState, previous: WsConnectivityState) => void,
): () => void {
  stateListeners.add(listener);
  return () => {
    stateListeners.delete(listener);
  };
}

/**
 * Fires once per outage, when `WS_STUCK_AFTER_ATTEMPTS` consecutive connection
 * attempts have failed without a `connected` in between. Re-arms on the next
 * successful connect.
 */
export function onWsReconnectStuck(listener: (attempts: number) => void): () => void {
  stuckListeners.add(listener);
  return () => {
    stuckListeners.delete(listener);
  };
}

/** Called only by the graphql-ws client handlers in `~/graphql/index.tsx`. */
export function reportWsConnectivity(next: WsConnectivityState) {
  const previous = state;
  state = next;

  if (next === "connected") {
    failedAttempts = 0;
    stuckReported = false;
    connectedListeners.forEach((listener) => listener());
  } else if (next === "closed" && previous === "connecting") {
    // An attempt that never reached `connected`.
    failedAttempts += 1;
    if (failedAttempts >= WS_STUCK_AFTER_ATTEMPTS && !stuckReported) {
      stuckReported = true;
      stuckListeners.forEach((listener) => listener(failedAttempts));
    }
  }

  stateListeners.forEach((listener) => listener(next, previous));
}

/** Test seam: reset module state between cases. */
export function resetWsConnectivityForTests() {
  state = "idle";
  failedAttempts = 0;
  stuckReported = false;
  connectedListeners.clear();
  stateListeners.clear();
  stuckListeners.clear();
}
